import uuid from 'uuid-random';
import deviceList, { phones, tablets } from './deviceList';
import { getUserAgent, isCrawlerBot, isVisualCustomizationMode } from 'js/utils/device.util';

import * as urlUtils from 'js/utils/url.util';

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';

const MOBILE = 'mobile';
const TABLET = 'tablet';
const DESKTOP = 'desktop';

const DEVICE_ID_LOCAL_STORAGE_KEY = 'hub2_device_uuid';
const DEVICE_ID_QUERY_PARAM = 'deviceId';

export const getId = () => {
    let deviceId = localStorage.getItem(DEVICE_ID_LOCAL_STORAGE_KEY);

    if (deviceId) {
        return deviceId;
    }

    const queryStringDeviceId = urlUtils.getURLParam(location.search, DEVICE_ID_QUERY_PARAM);

    deviceId = queryStringDeviceId ?? uuid();

    localStorage.setItem(DEVICE_ID_LOCAL_STORAGE_KEY, deviceId);

    return deviceId;
};

const isTouchDevice = () => 'ontouchstart' in document.documentElement;

const isIpadLikeDesktop = ua => isTouchDevice() && ua.includes('macintosh');

const findMatch = (rules, ua) => {
    for (const key in rules) {
        if (rules[key] && rules[key].test(ua)) {
            return key;
        }
    }

    return null;
};

const getAlias = (ua, name, osVersion) => {
    let list = deviceList.ua;
    let alias = Object.keys(list || {}).find(deviceAlias => list[deviceAlias].test(ua)) || 'other';

    if (alias === 'other') {
        list = deviceList.apple[name];
        alias = Object.keys(list || {}).find(apple => list[apple] === Math.max(screen.width, screen.height)) || alias;

        if (alias === 'ipad' && window.devicePixelRatio === 2) {
            alias = 'ipad_retina';
        }

        // iPhone6 & iPhone7 has the same resolution & userAgent,
        // need to distinguish it before sending on IMS
        if (alias === 'iphone_6' && parseInt(osVersion) >= 10) {
            alias = 'iphone_6_7';
        }

        if (alias === 'iphone_6_plus' && parseInt(osVersion) >= 10) {
            alias = 'iphone_6_7_plus';
        }
    }

    return alias;
};

const getOs = (ua) => {
    if (['iphone', 'ipod', 'ipad'].find(i => ua.includes(i)) || isIpadLikeDesktop(ua)) {
        return 'ios';
    }

    return ['android', 'windows', 'macintosh'].find(os => ua.includes(os));
};

const getOsVersion = (ua, os) => {
    let version = null;
    /* eslint-disable quote-props */
    const windowsVersions = {
        '10.': '10',
        '6.4': '10',
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7',
        '6.0': 'Vista',
        '5.2': 'XP 64-bit',
        '5.1': 'XP'
    };
    /* eslint-enable quote-props */

    switch (os) {
        case 'windows':
            version = windowsVersions[ua.split('windows nt ')[1]?.slice(0, 3)];
            break;
        case 'macintosh':
            version = ua.split('os ')[1]?.slice(0, 5).replace(/_/g, '.')
                .split(' ')[0];
            break;
        case 'ios':
            version = ua.split('os ')[1]?.slice(0, 5).replace(/_/g, '.')
                .split(' ')[0];
            break;
        case 'android':
            version = ua.split(/android([\s/])*/i)[2]?.slice(0, 5).replace(/_/g, '.')
                .split(' ')[0];
            version = /^[0-9.]*$/.test(version) ? version : parseFloat(version);
            break;
        case 'windowsphone':
            version = ua.split('windows phone ')[1]?.slice(0, 4);
            version = parseFloat(version);
            break;
        default:
            break;
    }

    return version;
};

const getBrowserLanguage = () => navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language || navigator.userLanguage;

const getBrowserName = (ua, os) => {
    const browser = (ua.match(/(chrome|crios|firefox|fxios|safari|msie|trident(?=\/))\/?\s*(\d+)/) || [])[1] || null;

    // Android native browser
    const resultChromeRegEx = /Chrome\/([\d.]+)/i.exec(ua);
    const chromeVersion = resultChromeRegEx === null ? null : parseFloat(resultChromeRegEx[1]);
    const isSamsungBrowser = /SamsungBrowser\/([\d.]+)/i.test(ua);

    // iOS standalone mode
    if (!browser && os === 'ios') {
        return 'safari';
    }

    // iOS Chrome
    if (browser === 'crios') {
        return 'chrome';
    }

    // iOS Firefox
    if (browser === 'fxios') {
        return 'firefox';
    }

    if ((!browser || browser === 'safari') && os === 'android' || (
        // native android browser for android with chrome web view
        chromeVersion != null && chromeVersion < 37 || isSamsungBrowser
    )) {
        return 'androidNative';
    }

    // Opera
    if (ua.includes('opr')) {
        return 'opera';
    }

    // Edge
    // Possible UA identifier tokens for Edge on Chromium: Edg, EdgiOS, EdgA
    if (ua.includes('edg')) {
        return 'edge';
    }

    // UC
    if (ua.includes('ucbrowser')) {
        return 'uc';
    }

    return ['msie', 'trident'].includes(browser) ? 'ie' : browser;
};

const getBrowserVersion = (ua, browserName) => {
    const re = /(opera|chrome|crios|safari|firefox|fxios|msie|trident(?=\/))\/?\s*(\d+)/i;
    const edgeRe = /\b(edge|edg|edga|edgeios)\/(\d+)/i;
    const ucRe = /\b(ucbrowser)\/([\d.]+)/i;
    let matched = ua.match(ucRe) || ua.match(edgeRe) || ua.match(re) || [];
    const { appName, appVersion } = window.navigator;
    let item;

    // applewebkit could not be joined with regexp above because it will be always the first match
    matched = matched.length ? matched : ua.match(/(applewebkit(?=\/))\/?\s*(\d+)/i) || [];

    matched = matched[2] ? [matched[1], matched[2]] : [appName, appVersion, '-?'];

    // 'safari/' part at UA-s means kernel version - 535-537...
    // so real version in all browsers goes after its name(Chrome/28), and only in safari - after "Version/"
    if ((item = ua.match(/version\/(\d+)/i)) != null && browserName === 'safari') {
        matched.splice(1, 1, item[1]);
    }

    if (!ua.includes('msie') && ua.includes('trident') && ua.includes('rv:11')) {
        matched[1] = '11';
    }

    return matched[1];
};

const getName = (ua, os, browserName) => {
    if (os === 'ios') {
        return ['iphone', 'ipad', 'ipod'].find(i => ua.includes(i)) || (isIpadLikeDesktop(ua) ? 'ipad' : '');
    }

    if (os === 'android') {
        let formFactor = ua.includes(MOBILE) || ua.includes('lenovo-a') ? 'Phone' : 'Tablet';

        // uc Browser in userAgent always writes Phone for both tablet and phone
        if (browserName === 'uc') {
            if (findMatch(phones, ua)) {
                formFactor = 'Phone';
            } else if (findMatch(tablets, ua)) {
                formFactor = 'Tablet';
            }
        }

        return `${os}${formFactor}`;
    }

    if (os === 'windows') {
        return os + (ua.includes('phone') ? 'Phone' : '');
    }

    return os === 'macintosh' ? os : null;
};

const getFormFactor = (name, ua) => {
    if (['iphone', 'ipod', 'androidPhone'].includes(name)) {
        return MOBILE;
    }

    if (['ipad', 'androidTablet'].includes(name) || isIpadLikeDesktop(ua)) {
        return TABLET;
    }

    return DESKTOP;
};

const supportedClientPlatformParams = [
    'ivr', // Interactive Voice Response
    'smsbetting', // SMS Betting
    'retailotc', // Retail Over The Counter
    'download', // Download
    'retailbip', // Retail Bet in Play
    'telebet', // Telebet
    'flash', // Flash
    'oncourse', // On Course
    'retailterminal', // Retail Terminal
    'system', // System
    'admin' // Admin
];

const getClientPlatform = (formFactor) => {
    const url = new window.URL(window.location.href);
    const clientPlatformFromUrl = url.searchParams.get('clientPlatform');

    return supportedClientPlatformParams.includes(clientPlatformFromUrl)
        ? clientPlatformFromUrl
        : formFactor === DESKTOP
            ? 'web'
            : MOBILE;
};

export const getDeliveryPlatform = () => {
    const url = new window.URL(window.location.href);
    const deliveryPlatformFromUrl = url.searchParams.get('deliveryPlatform');
    return deliveryPlatformFromUrl || 'Hub2';
};

const isLandscapeMode = angle => angle === null
    ? screen.availHeight < screen.availWidth
    : Boolean(angle / 90 % 2);

// not supported for iOS < 16.4
export const isScreenOrientationSupported = 'orientation' in screen && typeof screen.orientation.type === 'string';

export const getOrientation = () => {
    let landscapeMode;
    const isWindowOrient = 'orientation' in window; // Deprecated! Used in iOS, Opera for Android, Samsung Internet

    if (isScreenOrientationSupported) {
        landscapeMode = screen.orientation.type.includes('landscape');
    } else {
        const angle = isWindowOrient ? window.orientation : null;
        landscapeMode = isLandscapeMode(angle);
    }

    // userAgent in this mode is emulated, but screen is inherited from real device
    if (isVisualCustomizationMode()) {
        landscapeMode = window.innerHeight < window.innerWidth;
    }

    return {
        orientation: landscapeMode ? LANDSCAPE : PORTRAIT,
        isLandscape: landscapeMode,
        isPortrait: !landscapeMode
    };
};

/* Buzz override: removed formFactor option */
export const getBaseClasses = () => {
    const { os, osVersion, browserName, browserVersion, name, alias, orientation } = getCommonProps();

    const classes = [os, browserName, name, alias, orientation]
        .filter(item => item != null)
        .map(item => item.replace(' ', '_').replace('/7', ''));

    if (browserName && browserVersion) {
        classes.push((browserName + browserVersion).replace(' ', '_'));
    }

    if (os && osVersion) {
        classes.push(`${os}_${osVersion}`.replace(/[. ]/g, '_'));
    }

    return classes.join(' ');
};

const getCommonProps = () => {
    const ua = getUserAgent();
    const uaOrigin = getUserAgent(true);
    const os = getOs(ua);
    const osVersion = getOsVersion(ua, os);
    const browserName = getBrowserName(ua, os);
    const browserLanguage = getBrowserLanguage();
    const browserVersion = getBrowserVersion(ua, browserName);
    const name = getName(ua, os, browserName);
    const formFactor = getFormFactor(name, ua);
    const alias = getAlias(ua, name, osVersion);
    const { orientation, isLandscape, isPortrait } = getOrientation();

    return {
        ua, uaOrigin, os, osVersion, browserName, browserLanguage, browserVersion,
        name, formFactor, alias, orientation, isLandscape, isPortrait
    };
};

const getIsInWrapper = () => {
    const url = new window.URL(window.location.href);

    return Boolean(url.searchParams.get('deliveryPlatform'));
};

export const getDevice = () => {
    const common = getCommonProps();
    const { os, browserName, formFactor } = common;
    const clientPlatform = getClientPlatform(formFactor);
    const deliveryPlatform = getDeliveryPlatform();
    const isInWrapper = getIsInWrapper();
    const isCrawled = isCrawlerBot();

    return {
        ...common,
        isCrawled,
        isInWrapper,
        clientPlatform,
        deliveryPlatform,
        deviceId: getId(),
        isOtherBrowser: !['chrome', 'edge', 'firefox', 'safari'].includes(browserName),
        isOtherOs: !['android', 'ios', 'windows', 'macintosh'].includes(os),
        isTouchDevice: isTouchDevice(),
        isMobile: formFactor === MOBILE,
        isTablet: formFactor === TABLET,
        isDesktop: formFactor === DESKTOP
    };
};

export default getDevice;
